import { normalizeChargesForApi } from "@/lib/billing/normalize-charges";

import { PayinConfigCreate } from "@/types/billing";
import { ChargeCreate, ChargeSummary } from "@/types/charge";
import { LoyaltyPointState } from "@/types/loyalty-points";
import { CashSettlementPaymentSummary, PaymentSummary } from "@/types/payment";
import {
  ChargePaymentMethodResponse,
  Device,
  POSCancelResult,
  RainforestPayinConfig,
  RainforestPaymentMethodConfig,
} from "@/types/rainforest";
import type { ReaderPaymentProgress } from "@/types/reader-tipping";

import { requestData } from "@/api/api-config";

export async function createCharge(
  charge: ChargeCreate,
): Promise<ChargeSummary | null> {
  return await requestData<ChargeSummary>(
    "billing/payment-terminal/charge/create",
    "post",
    {
      json: charge,
    },
  );
}

export async function createCashSettledCharge(
  charges: ChargeCreate[],
  amount: number,
  paymentMedium: string,
  creditOverpaymentAmount?: number,
  patientId?: string,
  absorbedSourceChargeIds?: string[],
  roundCashTotal?: boolean,
  merchantAccountId?: string,
): Promise<CashSettlementPaymentSummary | null> {
  const params = new URLSearchParams({
    amount: amount.toString(),
    payment_medium: paymentMedium,
  });
  if (roundCashTotal) {
    params.append("round_cash_total", "true");
  }

  // Add credit overpayment parameters if provided
  if (creditOverpaymentAmount && creditOverpaymentAmount > 0) {
    params.append(
      "credit_overpayment_amount",
      creditOverpaymentAmount.toString(),
    );
  }
  if (patientId) {
    params.append("patient_id", patientId);
  }
  if (absorbedSourceChargeIds && absorbedSourceChargeIds.length > 0) {
    absorbedSourceChargeIds.forEach((id) =>
      params.append("absorbed_source_charge_ids", id),
    );
  }

  return await requestData<CashSettlementPaymentSummary>(
    `billing/payment-terminal/charge/create-cash-settled?${params.toString()}`,
    "post",
    {
      json: { charges: normalizeChargesForApi(charges), merchantAccountId },
    },
    false,
  );
}

export async function createPatientCreditSettledCharge(
  charges: ChargeCreate[],
  amount: number,
  creditIds?: string[],
  absorbedSourceChargeIds?: string[],
  merchantAccountId?: string,
): Promise<PaymentSummary | null> {
  const params = new URLSearchParams({
    amount: amount.toString(),
  });

  // Add credit IDs if provided (for selecting specific credits)
  if (creditIds && creditIds.length > 0) {
    creditIds.forEach((id) => params.append("credit_ids", id));
  }
  if (absorbedSourceChargeIds && absorbedSourceChargeIds.length > 0) {
    absorbedSourceChargeIds.forEach((id) =>
      params.append("absorbed_source_charge_ids", id),
    );
  }

  return await requestData<PaymentSummary>(
    `billing/payment-terminal/charge/create-patient-credit-settled?${params.toString()}`,
    "post",
    {
      json: { charges: normalizeChargesForApi(charges), merchantAccountId },
    },
    false,
  );
}

export interface LoyaltyPointsPaymentResponse extends PaymentSummary {
  loyaltyPoints?: LoyaltyPointState | null;
}

export async function createLoyaltyPointsSettledCharge(
  charges: ChargeCreate[],
  amount: number,
  grantIds?: string[],
  absorbedSourceChargeIds?: string[],
  merchantAccountId?: string,
): Promise<LoyaltyPointsPaymentResponse | null> {
  const params = new URLSearchParams({
    amount: amount.toString(),
  });

  if (grantIds && grantIds.length > 0) {
    grantIds.forEach((id) => params.append("grant_ids", id));
  }
  if (absorbedSourceChargeIds && absorbedSourceChargeIds.length > 0) {
    absorbedSourceChargeIds.forEach((id) =>
      params.append("absorbed_source_charge_ids", id),
    );
  }

  return await requestData<LoyaltyPointsPaymentResponse>(
    `billing/payment-terminal/charge/create-loyalty-points-settled?${params.toString()}`,
    "post",
    {
      json: { charges: normalizeChargesForApi(charges), merchantAccountId },
    },
    false,
  );
}

export type GiftCardSettlementSource =
  | { type: "code"; giftCardCode: string }
  | { type: "issued-card"; giftCardId: string };

function giftCardSettlementPayload(source: GiftCardSettlementSource) {
  switch (source.type) {
    case "code":
      return { gift_card_code: source.giftCardCode };
    case "issued-card":
      return { gift_card_id: source.giftCardId };
  }
}

export async function createGiftCardSettledCharge(
  charges: ChargeCreate[],
  amount: number,
  giftCardPayment: GiftCardSettlementSource,
  absorbedSourceChargeIds?: string[],
  merchantAccountId?: string,
): Promise<PaymentSummary | null> {
  const params = new URLSearchParams({ amount: amount.toString() });
  if (absorbedSourceChargeIds && absorbedSourceChargeIds.length > 0) {
    absorbedSourceChargeIds.forEach((id) =>
      params.append("absorbed_source_charge_ids", id),
    );
  }

  return await requestData<PaymentSummary>(
    `billing/payment-terminal/charge/create-gift-card-settled?${params.toString()}`,
    "post",
    {
      json: {
        ...giftCardSettlementPayload(giftCardPayment),
        merchantAccountId,
        charges: normalizeChargesForApi(charges),
      },
    },
    false,
  );
}

export async function createPaymentMethodConfig(
  patientId?: string,
): Promise<RainforestPaymentMethodConfig | null> {
  return await requestData<RainforestPaymentMethodConfig>(
    "billing/payment-terminal/create-payment-method-config",
    "post",
    {
      searchParams: {
        patient_id: patientId,
      },
    },
  );
}

/**
 * Give a card form minted with no patient to the patient just created, before
 * the card is submitted. On Stripe the card is otherwise never attached to a
 * Customer and can't be charged.
 */
export async function assignPaymentMethodConfig(
  paymentMethodConfigId: string,
  patientId: string,
): Promise<void> {
  await requestData(
    "billing/payment-terminal/assign-payment-method-config",
    "post",
    { json: { paymentMethodConfigId, patientId } },
    // Thrown, not swallowed: a card captured after a failed assign is the
    // single-use card this exists to prevent.
    false,
  );
}

export async function setupInvoicing(
  patientId: string,
  ruleSetId: string,
): Promise<string | null> {
  return await requestData<string>(
    "billing/payment-terminal/setup-invoicing",
    "post",
    {
      json: {
        patientId,
        ruleSetId,
      },
    },
  );
}

export async function createRainforestPayin(
  payinConfigCreate: PayinConfigCreate,
): Promise<RainforestPayinConfig | null> {
  return await requestData<RainforestPayinConfig>(
    "billing/payment-terminal/payin-config",
    "post",
    {
      json: {
        ...payinConfigCreate,
        charges: normalizeChargesForApi(payinConfigCreate.charges),
      },
    },
    false,
  );
}

export async function chargeCard(
  paymentMethodId: string,
  payinConfigId: string,
): Promise<ChargePaymentMethodResponse | null> {
  return await requestData<ChargePaymentMethodResponse>(
    `billing/payment-terminal/charge-card/${paymentMethodId}`,
    "post",
    {
      json: {
        payinConfigId,
      },
    },
    false,
  );
}

export async function listDevices(): Promise<Device[] | null> {
  return await requestData<Device[]>("billing/payment-terminal/list-devices");
}

export async function sendToPOS(
  payin_config_id: string,
  device_registration_id: string,
): Promise<Record<string, any> | null> {
  return await requestData<Record<string, any>>(
    "billing/payment-terminal/send-to-pos",
    "post",
    {
      json: {
        payin_config_id: payin_config_id,
        device_registration_id: device_registration_id,
      },
    },
    false,
  );
}

export async function cancelPOS(
  payin_config_id: string,
  device_registration_id: string,
  options?: { stopAffirm?: boolean },
): Promise<POSCancelResult | null> {
  return await requestData<POSCancelResult>(
    "billing/payment-terminal/cancel-pos",
    "post",
    {
      json: {
        payin_config_id: payin_config_id,
        device_registration_id: device_registration_id,
        // Only after staff confirm: it cancels an Affirm payment the patient
        // is still finishing on their phone.
        stop_affirm: options?.stopAffirm ?? false,
      },
    },
    false,
  );
}

/**
 * Moves a reader payment on that the clinic prices after the tap (tipping or
 * automatic surcharging on Stripe readers), in case its webhook is late.
 * Safe to call repeatedly; the server does each step once.
 */
export async function advancePOS(
  payin_config_id: string,
  device_registration_id: string,
): Promise<ReaderPaymentProgress | null> {
  return await requestData<ReaderPaymentProgress>(
    "billing/payment-terminal/advance-pos",
    "post",
    {
      json: {
        payin_config_id: payin_config_id,
        device_registration_id: device_registration_id,
      },
    },
    false,
  );
}

export async function markNoCharge(
  charges: ChargeCreate[],
): Promise<ChargeCreate[] | null> {
  const seenChargeIds = new Set<string>();
  const uniqueCharges = charges.filter((charge) => {
    if (!charge.id) return true;
    if (seenChargeIds.has(charge.id)) return false;
    seenChargeIds.add(charge.id);
    return true;
  });

  return await requestData<ChargeCreate[]>(
    "billing/payment-terminal/mark-no-charge",
    "post",
    { json: normalizeChargesForApi(uniqueCharges) },
  );
}

/**
 * Ask whether these outstanding charges can be merged into the checkout.
 *
 * Resolves when they can. Rejects with the server's 409 when they can't — some
 * blockers (a package redeemed elsewhere, a realized inventory drawdown, a
 * membership log) live in tables the client never fetches, so this is the only
 * way to know before payment. `catchError: false` so the caller sees the
 * rejection reason instead of a swallowed null.
 */
export async function validateAbsorbedCharges(
  sourceChargeIds: string[],
  targetPatientId: string,
): Promise<void> {
  await requestData<void>(
    "billing/payment-terminal/validate-absorbed-charges",
    "post",
    {
      json: {
        source_charge_ids: sourceChargeIds,
        target_patient_id: targetPatientId,
      },
    },
    false,
  );
}

/**
 * The last check before a newly typed card is submitted: are the charges this
 * payin folded in still what the cart saw? Checks the payin's own stored
 * payload — what the processor will actually collect — the same way the server
 * does before charging a saved card or presenting on a reader. Rejects with the
 * server's 409 (`reason`, `source_charge_id`) when something moved.
 */
export async function recheckPayinAbsorbedCharges(
  payinConfigId: string,
): Promise<void> {
  await requestData<void>(
    `billing/payment-terminal/payin-config/${payinConfigId}/recheck-absorbed-charges`,
    "post",
    undefined,
    false,
  );
}

export async function updatePayinConfigMetadata(
  payinConfigId: string,
  paymentMedium?: string,
  savePaymentMethod?: boolean,
): Promise<{ success: boolean; payin_config_id: string } | null> {
  const params = new URLSearchParams();
  if (paymentMedium !== undefined) {
    params.append("payment_medium", paymentMedium);
  }
  if (savePaymentMethod !== undefined) {
    params.append("save_payment_method", savePaymentMethod.toString());
  }

  return await requestData<{ success: boolean; payin_config_id: string }>(
    `billing/payment-terminal/payin-config/${payinConfigId}/metadata?${params.toString()}`,
    "patch",
  );
}
